import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, Search, FileText, RefreshCw, Download, Upload, FileDown, CheckSquare, Square, Calculator, Link2 } from 'lucide-react';

export default function NomenclatureContent({ ctx }) {
  const { collections, selectedCollection, setSelectedCollection, nomenclatures, categories, selectedCategory, setSelectedCategory, searchTerm, setSearchTerm, editingId, setEditingId, isCreating, setIsCreating, showArchived, setShowArchived, importing, selectedIds, selectionMode, setShowEstimatorDialog, setShowNomenclatureEstimateursDialog, setCurrentNomenclatureForEstimateurs, formData, setFormData, fetchCategories, fetchNomenclatures, handleCreate, handleUpdate, handleDelete, handleReactivate, startEdit, resetForm, filteredNomenclatures, getCategoryColor, getCategoryButtonStyle, toggleSelection, selectAll, deselectAll, toggleSelectionMode, handleExportCSV, handleExportExcel, handleDownloadTemplate, handleImport } = ctx;
  return (
    <>

      {/* Sélecteur de Collection */}
      {collections.length > 0 && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-semibold">Collection active :</Label>
              <select
                value={selectedCollection?.id || ''}
                onChange={(e) => {
                  const collection = collections.find(c => c.id === e.target.value);
                  setSelectedCollection(collection);
                  setFormData(prev => ({ ...prev, collection_id: collection?.id || '' }));
                }}
                className="flex-1 max-w-sm px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {collections.filter(c => c.is_active).map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name} ({collection.nomenclatures_count || 0} nomenclatures)
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            Gestion des Nomenclatures {selectedCollection ? `- ${selectedCollection.name}` : ''}
          </h2>
          <p className="text-sm text-gray-600">
            {filteredNomenclatures.length} {selectedCategory === 'PIB' ? 'missions' : 'activités'} {selectedCategory === 'all' ? 'au total' : `pour la catégorie ${selectedCategory}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Mode sélection */}
          <Button
            onClick={toggleSelectionMode}
            variant={selectionMode ? "default" : "outline"}
            size="sm"
            className="gap-2"
          >
            {selectionMode ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            {selectionMode ? `Sélection (${selectedIds.size})` : 'Sélectionner'}
          </Button>
          
          {/* Boutons de sélection */}
          {selectionMode && (
            <div className="flex gap-2">
              <Button
                onClick={selectAll}
                variant="outline"
                size="sm"
              >
                Tout sélectionner
              </Button>
              <Button
                onClick={deselectAll}
                variant="outline"
                size="sm"
              >
                Tout désélectionner
              </Button>
              <Button
                onClick={() => setShowEstimatorDialog(true)}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Link2 className="h-4 w-4" />
                Lier Simulateur (ancien)
              </Button>
              <Button
                onClick={() => {
                  const selectedNomenclatures = nomenclatures.filter(n => selectedIds.has(n.id));
                  setCurrentNomenclatureForEstimateurs(selectedNomenclatures.length === 1 ? selectedNomenclatures[0] : null);
                  setShowNomenclatureEstimateursDialog(true);
                }}
                variant="default"
                size="sm"
                className="gap-2"
              >
                <Calculator className="h-4 w-4" />
                Gérer Simulateurs ({selectedIds.size})
              </Button>
            </div>
          )}
          
          {/* Boutons d'export */}
          <div className="flex gap-2">
            <Button
              onClick={handleExportCSV}
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={selectionMode && selectedIds.size === 0}
            >
              <Download className="h-4 w-4" />
              CSV {selectionMode && selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
            <Button
              onClick={handleExportExcel}
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={selectionMode && selectedIds.size === 0}
            >
              <FileDown className="h-4 w-4" />
              Excel {selectionMode && selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
          </div>
          
          {/* Boutons d'import */}
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 relative"
              disabled={importing}
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Import...' : 'Importer'}
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleImport}
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={importing}
              />
            </Button>
          </div>
          
          {/* Autres boutons */}
          <Button
            onClick={() => {
              fetchCategories();
              fetchNomenclatures();
              toast.success('Données actualisées');
            }}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </Button>
          <Button
            onClick={() => {
              setIsCreating(true);
              resetForm();
            }}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom ou code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filtre par catégorie */}
            <div className="flex gap-2 items-center flex-wrap">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-gray-700 hover:bg-gray-800' : ''}
              >
                Toutes ({nomenclatures.length})
              </Button>
              {categories.map(cat => {
                const count = nomenclatures.filter(n => n.category === cat.name && (showArchived || n.is_active)).length;
                const isSelected = selectedCategory === cat.name;
                return (
                  <Button
                    key={cat.id}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.name)}
                    className={isSelected ? getCategoryButtonStyle(cat.name, true) : ''}
                  >
                    {cat.name} ({count})
                  </Button>
                );
              })}
              
              {/* Toggle pour afficher/masquer les archivés */}
              <div className="ml-4 flex items-center gap-2 border-l pl-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Afficher archivés</span>
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formulaire de création */}
      {isCreating && (
        <Card className="border-2 border-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Nouvelle Nomenclature</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsCreating(false);
                  resetForm();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Code</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: GO001, BLICS..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Nom de l'activité *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Terrassements"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Définition *</label>
                <Textarea
                  value={formData.definition}
                  onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
                  placeholder="Description complète de l'activité..."
                  rows={4}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Exclusions</label>
                <Textarea
                  value={formData.exclusions}
                  onChange={(e) => setFormData({ ...formData, exclusions: e.target.value })}
                  placeholder="Liste des exclusions (optionnel)..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formData.name || !formData.definition}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="mr-2 h-4 w-4" />
                Créer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des nomenclatures */}
      <div className="space-y-4">
        {filteredNomenclatures.map(nom => (
          <Card key={nom.id} className={!nom.is_active ? 'opacity-50' : ''}>
            <CardContent className="pt-6">
              {editingId === nom.id ? (
                // Mode édition
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Catégorie</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Code</label>
                      <Input
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Nom</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Définition</label>
                      <Textarea
                        value={formData.definition}
                        onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
                        rows={4}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Exclusions</label>
                      <Textarea
                        value={formData.exclusions}
                        onChange={(e) => setFormData({ ...formData, exclusions: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingId(null);
                        resetForm();
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Annuler
                    </Button>
                    <Button
                      onClick={() => handleUpdate(nom.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                // Mode lecture
                <div>
                  <div className="flex items-start justify-between mb-4">
                    {/* Checkbox de sélection */}
                    {selectionMode && (
                      <div className="mr-3 pt-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(nom.id)}
                          onChange={() => toggleSelection(nom.id)}
                          className="w-5 h-5 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getCategoryColor(nom.category)}>
                          {nom.category}
                        </Badge>
                        {nom.code && (
                          <Badge className="bg-slate-800 text-white font-semibold">{nom.code}</Badge>
                        )}
                        {nom.is_active ? (
                          <Badge className="bg-green-800 text-white font-semibold">En ligne</Badge>
                        ) : (
                          <Badge variant="destructive" className="font-semibold">Archivé</Badge>
                        )}
                        {nom.estimator?.enabled && (
                          <Badge className="bg-green-800 text-white gap-1 font-semibold">
                            <Calculator className="h-3 w-3" />
                            Simulateur
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold uppercase">{nom.name}</h3>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCurrentNomenclatureForEstimateurs(nom);
                          setShowNomenclatureEstimateursDialog(true);
                        }}
                        title="Gérer les estimateurs"
                      >
                        <Calculator className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(nom)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {nom.is_active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(nom.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(nom.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Définition :</span>
                      <p className="text-gray-700 mt-1">{nom.definition}</p>
                    </div>

                    {nom.exclusions && (
                      <div>
                        <span className="font-medium text-red-600">Exclusions :</span>
                        <p className="text-gray-700 mt-1">{nom.exclusions}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredNomenclatures.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Aucune nomenclature trouvée</p>
            </CardContent>
          </Card>
        )}
      </div>

    </>
  );
}
